import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';

import { RequestQueryKeysE } from 'declarations/enums/react-query';
import type { ApiErrorI } from 'declarations/interfaces/errors';
import type { NoopT } from 'declarations/types/common';
import type { SelectOptionT } from 'declarations/types/select';
import { getErrorMessage, getValidationErrorMessage } from 'helpers/apiError';
import { uploadTicketAttachmentsFromFiles } from 'helpers/uploadTicketAttachmentsFromFiles';
import { useGetAttachmentTypesApi } from 'hooks/api/useAttachments';
import { updateSnackbarNotification } from 'reduxStore/features/notifications/notificationsSlice';
import { useAppDispatch } from 'reduxStore/hooks';

import type { TicketAttachmentFormValuesT } from '../declarations';

type UseAddAttachmentPopupT = {
  handleSubmitForm: (data: TicketAttachmentFormValuesT, onClose: NoopT) => Promise<void>;
  isCreatingAttachment: boolean;
  handleClosePopup: (onClosePopupCb: NoopT) => NoopT;
  attachmentTypesOptions: SelectOptionT[];
  isLoadingAttachmentTypesOptions: boolean;
};

type UseAddAttachmentPopupParamsT = {
  ticketId: string;
  categoryName: string;
  assignToWarrCloud: boolean;
};

const useAddAttachmentPopup = ({
  ticketId,
  categoryName,
  assignToWarrCloud,
}: UseAddAttachmentPopupParamsT): UseAddAttachmentPopupT => {
  /**
   * Hooks
   */
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const [isUploading, setIsUploading] = useState(false);

  /**
   * Api hooks
   */
  const attachmentTypeResult = useGetAttachmentTypesApi();

  /**
   * Variables
   */
  const attachmentTypesOptions = useMemo(
    () =>
      attachmentTypeResult.data
        ? attachmentTypeResult.data?.map(({ attachmentTypeCode, attachmentTypeDescription }) => ({
            label: attachmentTypeDescription,
            value: attachmentTypeCode,
          }))
        : [],
    [attachmentTypeResult.data]
  );

  const invalidateAfterAttachment = useCallback(
    (assignToOppositeUser: boolean): void => {
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTicketMessages]);
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTicketAttachments]);
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTickets]);
      if (assignToOppositeUser) {
        queryClient.invalidateQueries([RequestQueryKeysE.FetchTicket]);
      }
    },
    [queryClient]
  );

  /**
   * Handlers
   */
  const handleClosePopup = useCallback(
    (onClosePopupCb: NoopT) => (): void => {
      onClosePopupCb();
    },
    []
  );

  const handleSubmitForm = useCallback(
    async (data: TicketAttachmentFormValuesT, onClose: NoopT) => {
      setIsUploading(true);
      try {
        await uploadTicketAttachmentsFromFiles({
          ticketId,
          attachmentTypeCode: data.attachmentTypeCode,
          notes: data.note || categoryName,
          assignToOppositeUser: assignToWarrCloud,
          attachmentFileList: data.attachmentFile,
          attachmentPhotoList: data.attachmentPhoto,
        });

        invalidateAfterAttachment(assignToWarrCloud);

        dispatch(
          updateSnackbarNotification({
            snackbarMessage: 'Attachment successfully published',
            snackBarSeverity: 'success',
          })
        );
        onClose();
      } catch (error) {
        const errorMsg =
          getErrorMessage(error as ApiErrorI) || getValidationErrorMessage(error as ApiErrorI);
        dispatch(
          updateSnackbarNotification({
            snackbarMessage: `Something went wrong. ${errorMsg || ''}`,
            snackBarSeverity: 'error',
          })
        );
        onClose();
      } finally {
        setIsUploading(false);
      }
    },
    [assignToWarrCloud, categoryName, dispatch, invalidateAfterAttachment, ticketId]
  );

  return {
    handleSubmitForm,
    isCreatingAttachment: isUploading,
    handleClosePopup,
    attachmentTypesOptions,
    isLoadingAttachmentTypesOptions: attachmentTypeResult.isLoading,
  };
};

export default useAddAttachmentPopup;
